/**
 * 지갑 화면의 판정 규칙 -- **순수 함수만** 둔다. React도 통신도 모른다.
 *
 * 파일을 줄이려고 뺀 게 아니라 규칙이 **한 군데**만 있어야 해서다. 지금은 wallet.page
 * 하나가 쓰지만, user.page 목록에 "정지된 지갑 있음" 뱃지를 붙이는 순간 같은 판정이
 * 복사된다. 복사된 판정은 한쪽만 고쳐지고 그 어긋남은 조용하다 -- 지금 wallet.page가
 * isStale을 인라인으로 들고 있는 게 그 씨앗이었다.
 *
 * == 상태를 하나로 접지 않는다 ==
 *
 * 세 축이 서로 독립이다.
 *
 *   배선(link)  -- 스냅샷이 오고 있나. 트레이더/브로커 쪽 사실
 *   운영(run)   -- wallets.status. 사람이 정한 운영 의도
 *   정지(halt)  -- wallets.halt_*. 장부와 실물이 어긋나 시스템이 멈춘 것
 *
 * 하나의 값으로 접으면 "정지 중인데 갱신도 멈춤"을 표현할 수 없고, 그 상태가 정확히
 * 사람이 가장 먼저 알아야 하는 상태다(요청을 넣어도 판정할 주체가 없다).
 */

/** 트레이더 발행 주기가 5초다. 그 6배를 넘겼으면 갱신이 멈춘 것으로 본다.
 *
 * 서버(BrokerController)는 낡은 스냅샷도 거르지 않고 그대로 보낸다 -- 조용히 빼면 웹이
 * "대상이 없다"와 "갱신이 멈췄다"를 구분할 수 없기 때문이다. 그 판단을 여기서 한다. */
export const STALE_MILLIS = 30000

/** uptAt은 스냅샷이 와야 바뀐다. 트레이더가 죽으면 아무것도 안 오므로 상태 변화도 리렌더도
 * 없어 **갱신 멈춤이 영영 안 뜬다.** 화면이 스스로 시계를 돌려 주기적으로 다시 판정한다. */
export const TICK_MILLIS = 5000

/** DB 재조회 주기. 트레이더 `refreshWalletControl`과 **같은 값**이어야 한다 -- 그보다 촘촘히
 * 물어봐야 새 판정이 있을 수 없고, 그보다 성기면 사람이 결과를 늦게 본다. */
export const POLL_MILLIS = 10000

/** 시세(`price`)가 이만큼 낡으면 흐리게 표시한다.
 *
 * 출처가 1분봉이라 정상 상태에서도 최대 1분 남짓 낡아 있다 -- 그건 지연이 아니라 이 값의
 * 성질이다. 봉이 **세 개** 빠졌다면 수집기가 밀린 것이고, 그건 사실이라 드러내야 한다
 * (`TfCandles.warnIfStale`이 트레이더 쪽에서 같은 판정을 로그로 남긴다). */
export const PRICE_STALE_MILLIS = 3 * 60 * 1000

/** 미실현 손익(`unrealizedAt`)이 이만큼 낡으면 흐리게 표시한다.
 *
 * **정상값이 하나로 안 정해진다.** 갱신 주체인 `scanVanishedPosition`이 1분 주기지만
 * non-STOP 미결이 살아 있으면 조기 리턴한다 -- 진입 지정가가 걸려 있으면 `entryTimeoutMillis`
 * (10분)까지 정상적으로 안 갱신된다.
 *
 * 그래서 이 임계는 "고장"이 아니라 **"슬슬 의심해볼 나이"**다. 진입 타임아웃의 절반으로 잡아,
 * 넘으면 값을 지우지 않고 흐리게만 한다 -- 지우면 사람이 "손익이 0이 됐다"로 읽는다. */
export const UNREALIZED_STALE_MILLIS = 5 * 60 * 1000

/** 배선 상태 -- 스냅샷이 오고 있는가. */
export const Link = {
  OK: 'OK',
  STALE: 'STALE',     // 스냅샷은 있는데 낡았다
  DOWN: 'DOWN',       // DB엔 있는데 스냅샷이 없다 = 미기동
  UNKNOWN: 'UNKNOWN', // 우리가 못 받는 중이라 판정을 보류한다
}

export const LinkLabel = {
  OK: '정상',
  STALE: '갱신 멈춤',
  DOWN: '미기동',
  UNKNOWN: '알 수 없음',
}

/** **고를 값의 이름**이다 -- 드롭다운 항목. 상태명이지 경과가 아니다.
 *
 * 셋이 같은 축이어야 한다: `가동` / `신규진입 정지` / `종료`. FIN만 진행형('종료 중')이면
 * "무엇을 고르는가"가 어긋난다 -- 사람이 고르는 건 도달할 상태다. */
export const RunLabel = {
  ACTIVE: '가동',
  NO_ENTRY: '신규진입 정지',
  FIN: '종료',
}

/** **지금 어떤 상태인가** -- 화면에 그릴 이름. `RunLabel`과 일부러 다르다.
 *
 * FIN에는 성격이 다른 두 구간이 있고, 그 차이가 사람의 행동을 바꾼다.
 *
 * {{{
 *   종료 중   아직 청산 주문이 나갈 수 있다(대개 1분 안). 지금 직접 거래하면 겹친다
 *   종료됨    손 뗐다(finSettled). 이후 트레이더가 이 지갑의 거래소를 안 본다
 * }}}
 *
 * 나머지 상태는 구간이 하나라 `RunLabel` 그대로다.
 *
 * ⚠ **`settled`는 스냅샷에서 온다.** 트레이더가 안 떠 있으면 스냅샷이 없어 `false`이고,
 * 그러면 여기서 "종료 중"이라고 말한다 -- 실제로는 아무것도 정리되고 있지 않다. 그 거짓말을
 * 여기서 막지 않는 이유는 **링크 뱃지가 이미 "미기동"을 말하고 있기 때문**이다. 두 축을
 * 각자 그리는 게 이 화면의 원칙이고(§상태를 하나로 접지 않는다), 여기서 겹쳐 판정하면
 * 그 원칙이 무너진다. */
export const runStateLabel = (run, settle) => {
  if (run !== 'FIN') {
    return RunLabel[run] || run
  }
  return settle.settled ? '종료됨' : '종료 중'
}

/**
 * 이 화면이 보여줄 `wallets.run_mode`.
 *
 * `LiveRunner.RunModes`는 `MAINNET | TESTNET | DRILL` 셋이고 DRILL도 정상적인 값이다 --
 * 별도 프로세스가 `DryRunExchangeAdapter`로 돌린다. 다만 **이 화면은 운영 화면이라**
 * 드릴 지갑까지 섞으면 실거래 지갑이 그 안에 묻힌다. 드릴 프로세스는 상시 가동이 아니라서
 * 대개 "미기동"으로만 줄줄이 뜨고, 그게 진짜 미기동을 눈에 안 띄게 만든다.
 *
 * `InitializationListener`가 dd 행을 시딩할 때 긋는 선과 **같은 선이다**(MAINNET/TESTNET).
 * 드릴 지갑을 봐야 할 화면이 생기면 그때 이 목록을 인자로 받으면 된다.
 */
/**
 * 거래소 코드 -> 사람이 읽는 이름. **`wallets.exchange_id`(3글자 상수)의 표시 전용 사본이다.**
 *
 * 판정에 쓰지 않는다 -- 코드가 정본이고 이건 글자다. 모르는 코드는 **코드를 그대로 보여준다**
 * (`labelOfExchange`): 새 거래소를 붙인 날 여기 한 줄이 빠져도 화면이 빈칸이 되면 안 된다.
 * 빈칸은 "거래소가 없다"로 읽히고, 그건 지갑이 어디로 주문을 내는지를 가리는 방향이다.
 */
export const ExchangeLabel = { BIN: '바이낸스', BTG: '비트겟' }

export const labelOfExchange = (code) => ExchangeLabel[code] || code || '(미지정)'

export const LIVE_RUN_MODES = ['MAINNET', 'TESTNET']

/**
 * 총액에 **더할** `wallets.run_mode`. `LIVE_RUN_MODES`와 **일부러 다르다.**
 *
 * 두 질문이라서다.
 *
 *   목록  "어느 지갑이 지금 어떤 상태인가"  -> 테스트넷도 봐야 한다(가동 중인지 알아야 하니까)
 *   총액  "내 돈이 얼마인가"                -> 테스트넷은 내 돈이 아니다
 *
 * 상수 하나로 겸하면 둘 중 하나가 반드시 틀린다. 실제로 틀렸다 -- 실계좌 넷이 0.00인데
 * 테스트넷 4,273.39가 "확정 잔고 합"에 들어가 **계좌가 비었다는 사실이 화면에서 가려졌다.**
 *
 * `totalsOf`가 드릴을 거른 근거(`DryRunExchangeAdapter`의 가짜 잔고가 총액에 낀다)가 그대로
 * 걸린다. 테스트넷 잔고는 거래소가 실제로 돌려준 값이라 **출처는 다르지만 총자산이 아니라는
 * 점에서는 같다.**
 *
 * ⚠ 축이 섞이는 문제도 같이 닫힌다. 마스터(현물)는 `master_accounts`에 `run_mode`가 없고
 * **항상 MAINNET이다**(마스터관측 문서 §3 -- "그 전제는 컬럼이 아니라 합산 쿼리에 적는다").
 * 예전 총액은 `MAINNET+TESTNET 선물 + MAINNET 현물`이라 그 전제를 정면으로 어겼다.
 */
export const TOTAL_RUN_MODES = ['MAINNET']

/** 트레이더가 `rejectHaltClear`로 쓰는 접두사. **우리 코드가 만드는 문자열**이라 판별에 써도
 * 된다(LiveService.rejectHaltClear -> `s"clear rejected: $reason"`).
 *
 * 이걸로 가르는 이유: `halt_detail` 한 칸에 두 종류가 들어온다 -- 정지 자체의 사유와,
 * 해제 시도가 거절된 사유. 사람에게는 전혀 다른 소식이라 화면에서 갈라야 한다. */
const REJECT_PREFIX = 'clear rejected: '

/**
 * Scala `Option` 컬럼을 정규화한다.
 *
 * `OptionSerializer`(coin-common)가 두 가지를 한다.
 *
 *   None    -> `null`이 아니라 **빈 문자열** `""`
 *   Some(x) -> 값이 아니라 **문자열** (`context.serialize(o).getAsString`)
 *
 * 그래서 `halt_at`/`halt_clear_req_at` 같은 숫자 컬럼이 `"1754650000000"`으로 온다.
 * 그대로 비교하면 **사전순**이 된다 -- `"9" > "10"`이 참이다. 이 화면의 판정이 통째로
 * `halt_clear_req_at > halt_at` 하나에 걸려 있으므로 여기서 반드시 숫자로 만든다.
 *
 * `""`를 `??`로 못 거른다는 것도 같은 자리의 함정이다(빈 문자열은 nullish가 아니다).
 *
 * ⚠ 만약 응답에 `{"value":...}` 모양이 보이면 이 어댑터가 `Option[Long]` 같은 파라미터화
 * 타입에 안 붙은 것이다. 그건 여기서 받아낼 게 아니라 서버에서 고칠 일이다 -- 그때
 * 아래 함수들은 null을 돌려주고, 증상은 "정지는 보이는데 대기 표시가 안 뜬다"로 나온다.
 */
const optNum = (v) => {
  if (v === null || v === undefined || v === '') {
    return null
  }
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

const optStr = (v) => {
  if (v === null || v === undefined || v === '') {
    return null
  }
  return String(v)
}

/**
 * 배선 판정.
 *
 * **연결이 없을 때 DOWN을 붙이지 않는 게 요점이다.** 브로커가 끊기면 전 지갑이 동시에
 * 소식을 못 받는데, 그때 지갑마다 "미기동"을 붙이면 거짓말이 된다 -- 트레이더는 멀쩡히
 * 돌고 있을 수 있고 못 받는 건 우리 쪽이다. 원인이 화면에 있는데 지갑을 의심하게 만든다.
 *
 * 다만 **신선한 스냅샷이 손에 있으면 연결 여부와 무관하게 OK다.** 30초 안에 온 값이
 * 있다는 것 자체가 "그 시점에 트레이더가 살아 있었다"는 사실이고, 그 사실은 지금 우리가
 * mqtt에 붙어 있는지와 상관없다. 이렇게 안 하면 페이지를 열 때마다 -- 첫 스냅샷은 /broker
 * 캐시로 먼저 오고 mqtt 접속은 그 뒤에 맺어진다 -- 전 지갑이 잠깐 "알 수 없음"으로 깜빡인다.
 */
export const linkOf = (snap, connected, nowMs) => {
  if (!snap) {
    return connected ? Link.DOWN : Link.UNKNOWN
  }
  if (nowMs - snap.uptAt > STALE_MILLIS) {
    return connected ? Link.STALE : Link.UNKNOWN
  }
  return Link.OK
}

/**
 * 정지 축 -- **DB 행에서만** 만든다. 스냅샷의 `halted`는 쓰지 않는다.
 *
 * 스냅샷이 5초로 더 빠른데도 DB를 정본으로 삼는 이유가 셋이다.
 *
 *  1. **거절 사유가 DB에만 있다.** `rejectHaltClear`가 `halt_detail`에 쓰고, 스냅샷에는
 *     그 칸이 없다. 요청을 냈는데 결과를 못 보면 사람은 10초마다 버튼을 다시 누른다.
 *  2. **판정 대기 여부도 DB에만 있다** -- `halt_clear_req_at > halt_at`.
 *  3. **미기동 지갑은 스냅샷이 아예 없다.** 정지된 채로 트레이더가 죽은 지갑이야말로
 *     사람이 봐야 하는데, 스냅샷 기준이면 그 지갑이 화면에서 사라진다.
 *
 * 두 출처를 섞지 않는다 -- 섞으면 "지금 이 값이 누구 것인가"를 아무도 말할 수 없게 된다
 * (LiveService의 halt 컬럼 소유권 주석이 경계하는 바로 그 모양이다). 스냅샷은 대신
 * **재조회 트리거**로만 쓴다(useWallets 참고).
 */
export const haltOf = (row) => {
  const code = optStr(row.haltCode)
  if (!code) {
    return { halted: false, pending: false, restartRequired: false }
  }

  // halt_at이 비어 있는 행은 원래 생길 수 없지만(updateHalt가 항상 같이 쓴다) 사람이 손으로
  // 만들 수 있다. 트레이더도 그때 0으로 보고 요청을 통과시키므로(handleHaltClearRequest)
  // 화면도 같은 규칙을 쓴다 -- 다르게 보면 화면과 실제 판정이 어긋난다.
  const at = optNum(row.haltAt) ?? 0
  const reqAt = optNum(row.haltClearReqAt)
  const detail = optStr(row.haltDetail) ?? ''
  const rejected = detail.startsWith(REJECT_PREFIX)

  return {
    halted: true,
    code,
    at,
    reqAt,
    detail,
    /** 아직 판정 안 한 요청. 거절되면 트레이더가 halt_at을 요청 시각 뒤로 밀어 이 값이 꺼진다. */
    pending: reqAt !== null && reqAt > at,
    /** 거절 사유(접두사 뗀 것). 없으면 null -- 정지 사유 원문은 detail에 그대로 있다. */
    rejectedReason: rejected ? detail.slice(REJECT_PREFIX.length) : null,
    /**
     * 눌러도 소용없는 거절인가.
     *
     * ⚠ 트레이더가 만든 **영어 문장을 부분 일치로 판별한다.** 문구가 바뀌면 조용히 false가
     * 되는데, 그 방향이 안전한 쪽이라 이렇게 둔다 -- 최악이 "헛 요청 한 번"이고, 그 비용은
     * 10초에 한 번 queryPosition(weight 5)이다. 반대로 판별을 강하게 걸었다가 오탐하면
     * 풀 수 있는 정지를 화면이 막아버린다.
     *
     * 근거 문자열: verifyHaltCleared -> `s"$code is not clearable while running -- restart required"`
     */
    restartRequired: rejected && detail.includes('restart required'),
  }
}

/**
 * 해제 요청을 지금 눌러도 되는가. 안 되면 이유를 같이 준다 -- 비활성 버튼만 있고 왜 그런지가
 * 없으면 사람은 화면이 고장 난 줄 안다.
 *
 * **미기동에서 막는 게 이 함수의 존재 이유다.** 정지 정보는 DB에 있으니 화면에 보이고
 * 버튼도 눌리고 요청도 DB에 잘 적힌다. 그런데 그걸 확인할 트레이더가 없어서 판정이 영원히
 * 안 나고, 화면은 "대기 중"에서 멈춘다. 눌리기 전에 말해주는 게 맞다.
 *
 * STALE은 **막지 않고 알리기만 한다.** 낡은 스냅샷은 트레이더가 죽은 것일 수도 있고
 * 브로커가 잠깐 끊긴 것일 수도 있어 단정할 수 없다. 단정할 수 없으면 사람이 정하게 둔다.
 */
export const canRequestHaltClear = (link, halt) => {
  if (!halt.halted) {
    return { ok: false, why: '' }
  }
  if (link === Link.DOWN) {
    return { ok: false, why: '트레이더가 떠야 처리됩니다 -- 지금 요청하면 판정 없이 대기만 합니다' }
  }
  if (link === Link.UNKNOWN) {
    return { ok: false, why: '연결 상태를 확인하는 중입니다' }
  }
  if (halt.pending) {
    return { ok: false, why: '판정 대기 중입니다 -- 최대 10초쯤 걸립니다' }
  }
  if (halt.restartRequired) {
    return { ok: false, why: '이 사유는 가동 중에 풀 수 없습니다 -- 재기동이 필요합니다' }
  }
  return { ok: true, why: '' }
}

/**
 * status를 이 값으로 바꾸면 무슨 일이 벌어지는가 -- **확인 창에 그대로 실을 문구**를 만든다.
 *
 * == 왜 확인을 받나 ==
 *
 * 셋이 드롭다운에 나란히 있으면 무게가 같아 보이는데 실제로는 다르다.
 *
 *   ACTIVE   -- 신규 진입을 다시 허용. 가역
 *   NO_ENTRY -- 신규 진입만 차단. 보유 포지션의 청산·스탑은 그대로 돈다. 가역
 *   FIN      -- **보유 포지션을 전량 시장가로 청산한다.** 되돌릴 수 없다
 *
 * 주인 지시로 셋 다 확인을 받되, 문구를 같게 두면 확인이 요식이 되어 정작 FIN에서 안 멈춘다.
 * 그래서 FIN만 `destructive`를 세우고 본문도 갈라 놓는다.
 *
 * == 시점을 같이 말하는 이유 ==
 *
 * FIN은 **누른 순간 청산되지 않는다.** 트레이더가 `refreshWalletControl`(10초)로 상태를 읽고
 * 그 다음 `scanWalletFin`(1분)에서 청산한다.
 *
 * **정지 중이어도 나간다**(2026-08). FIN은 halt를 무시한다 -- `scanWalletFin`이
 * `ignoreHalt`로 부르기 때문이다. 그 전까지는 정지가 청산을 막아서 "정지가 풀리는 즉시
 * 청산"이라고 안내했는데, 지금은 사실이 아니다.
 *
 * 안 나가는 경우는 **미기동 하나뿐**이고, 그건 "아무 일도 안 일어났네"로 보이는데 실은
 * **예약된 상태**다. 말 안 하면 사람이 잊어버리고 나중에 트레이더를 띄웠을 때 예상 못 한
 * 청산을 본다. 지연된 지뢰라 미리 말한다.
 *
 * == FIN이 건드리는 범위 ==
 *
 * **엔진이 낸 것뿐이다.** 우리 장부(미러/주문)에 없는 포지션은 손대지 않고, 정리가 끝나면
 * 그 지갑의 거래소 접근을 통째로 멈춘다(`finSettled`). 그래서 "이 뒤로 사람이 뭘 하든
 * 트레이더는 관여하지 않는다"까지 말해 준다 -- 청산만 알리면 사람은 지갑이 계속 감시되고
 * 있다고 생각한다.
 *
 * @param next    바꾸려는 status
 * @param link    `linkOf` 결과
 * @param halt    `haltOf` 결과
 * @param current 지금 status. FIN에서 돌아오는 경우에만 쓴다
 */
export const statusChangeIntent = (next, link, halt, current) => {
  const down = link === Link.DOWN
  const unknown = link === Link.UNKNOWN

  // 시점 한 줄 -- 세 상태 모두 같은 배선을 타므로 여기서 한 번만 만든다.
  const timing = () => {
    if (down) {
      return '지금은 트레이더가 떠 있지 않습니다 -- 값은 저장되고, 기동될 때 적용됩니다.'
    }
    if (unknown) {
      return '연결 상태를 확인하는 중입니다 -- 값은 저장되고, 트레이더가 최대 10초 안에 읽습니다.'
    }
    return '트레이더가 최대 10초 안에 읽습니다.'
  }

  if (next === 'FIN') {
    const lines = ['엔진이 낸 포지션과 미결 주문을 전부 종료합니다. 되돌릴 수 없습니다.']

    if (down) {
      // 예약 상태 -- "아무 일도 안 일어났다"로 보이지만 기동되면 바로 나간다.
      lines.push('지금은 트레이더가 떠 있지 않아 당장은 아무 일도 일어나지 않습니다. 기동되는 즉시 청산합니다.')
    }
    else if (halt.halted) {
      // 정지는 더 이상 청산을 막지 않는다. 그래도 한 줄 말해두는 이유: 사람이 "정지 중인데
      // 청산이 되나"를 확인 창에서 묻게 되고, 답이 없으면 취소를 누른다.
      lines.push('거래 정지 중이지만 종료는 그대로 진행됩니다 -- 정지가 청산을 막지 않습니다.')
    }
    else {
      lines.push('상태를 읽은 뒤 다음 종료 스캔에서 청산합니다 -- 대략 1분 안입니다.')
    }

    // 범위를 말해두지 않으면 "지갑을 계속 감시한다"고 오해한다. 이 뒤로는 트레이더가 이
    // 지갑의 거래소를 아예 안 본다 -- 사람이 직접 연 포지션도 마찬가지다.
    lines.push('종료 후에는 이 지갑의 거래를 더 이상 보지 않습니다 -- 직접 주문을 내거나 포지션을 열어도 트레이더는 관여하지 않습니다.')

    return {
      destructive: true,
      title: '전량 청산하고 종료할까요?',
      lines,
      confirmLabel: '청산하고 종료',
    }
  }

  if (next === 'NO_ENTRY') {
    return {
      destructive: false,
      title: '신규 진입을 막을까요?',
      lines: [
        '신규 진입과 증량만 막습니다. 보유 포지션의 청산·스탑은 그대로 돕니다.',
        timing(),
      ],
      confirmLabel: '신규진입 정지',
    }
  }

  if (next === 'ACTIVE') {
    const lines = ['신규 진입을 다시 허용합니다.', timing()]

    // FIN에서 돌아오는 건 **손 뗐던 지갑을 다시 잡는 것**이다. 그동안 사람이 연 포지션이
    // 남아 있으면 트레이더가 그걸 "설명 안 되는 포지션"으로 보고 곧바로 정지를 건다
    // (scanVanishedPosition -> POSITION_UNKNOWN). 겪고 나서 알면 사고로 읽힌다.
    if (current === 'FIN') {
      lines.push('종료 후 직접 연 포지션이 남아 있으면 트레이더가 그걸 설명할 수 없어 거래를 정지시킵니다 -- 먼저 정리해두세요.')
    }

    // 거래 정지는 별개 축이다. 여기서 안 풀린다는 걸 말해두지 않으면 "가동으로 바꿨는데
    // 왜 안 도나"가 된다 -- 해제 요청은 아래 정지 안내의 버튼이 따로 맡는다.
    if (halt.halted) {
      lines.push('거래 정지는 이걸로 풀리지 않습니다 -- 정지 해제는 따로 요청해야 합니다.')
    }
    return {
      destructive: false,
      title: '가동으로 되돌릴까요?',
      lines,
      confirmLabel: '가동',
    }
  }

  // 서버가 허용값을 늘렸는데 화면이 문구를 안 가진 경우. 막지 않고 일반 문구로 통과시킨다 --
  // 여기서 막으면 서버가 정본인데 화면이 거부하는 모양이 된다.
  return {
    destructive: false,
    title: `상태를 ${next}로 바꿀까요?`,
    lines: [timing()],
    confirmLabel: '변경',
  }
}

/**
 * DB 행(목록의 정본)에 스냅샷(실시간 값)을 덮는다.
 *
 * **목록이 DB에서 나오는 게 이 함수의 핵심이다.** 옛 화면은 목록까지 스냅샷에서 만들어서
 * 트레이더가 죽으면 지갑이 화면에서 통째로 사라졌다 -- "지갑 없음"과 "트레이더 미기동"이
 * 같은 화면이었다. 쏠 주체가 없다는 게 미기동의 정의라, 미기동은 스냅샷으로 표현할 수 없다.
 *
 * `orphans`는 반대 경우다 -- 스냅샷은 오는데 DB 행이 없다. 지갑을 지웠는데 트레이더가 아직
 * 그 지갑을 돌리고 있다는 뜻이라 **버리면 안 되는 사건이다.** 목록에서 조용히 빼면 실거래가
 * 도는 지갑이 아무 화면에도 안 나온다.
 *
 * **run_mode 필터는 목록에만 걸고 orphan 판정에는 안 건다.** 걸어버리면 드릴 프로세스가
 * 돌 때 그 스냅샷이 "DB에 없는 지갑"으로 잡혀 가짜 경보가 뜬다 -- 스냅샷에는 run_mode가
 * 실려 있지 않아 화면이 그걸 되받을 방법도 없다. 안 보여주는 것과 모르는 것은 다르다.
 *
 * @param rows       /wallet/user/{userId} 응답 (WalletsRow 배열)
 * @param symbolRows /wallet/symbols/user/{userId} 응답 (WalletSymbolsRow 배열)
 * @param snapsById  useBroker의 records.wallet -- **키가 `stateId`("지갑/심볼")다**
 * @param nowMs      판정 기준 시각. 인자로 받아야 이 함수가 순수해지고 테스트가 가능하다
 * @param runModes   목록에 남길 run_mode. 기본은 운영 지갑만
 */
export const mergeWallets = (rows, symbolRows, snapsById, connected, nowMs, runModes = LIVE_RUN_MODES) => {
  const snaps = snapsById || {}
  const seen = new Set()

  const wsByWallet = new Map()
  ;(symbolRows || []).forEach((ws) => {
    const k = String(ws.walletId)
    if (!wsByWallet.has(k)) {
      wsByWallet.set(k, [])
    }
    wsByWallet.get(k).push(ws)
  })

  const all = (rows || []).map((row) => {
    // 자리 목록은 **DB가 정본이다.** 스냅샷에서 만들면 미기동 심볼이 화면에서 사라진다 --
    // 정지된 채 트레이더가 죽은 심볼이야말로 사람이 봐야 하는 것이고, 그게 `haltOf`가
    // DB를 정본으로 삼는 이유와 같다.
    const wsRows = (wsByWallet.get(String(row.id)) || [])
      .slice()
      .sort((a, b) => (a.symbolId ?? 0) - (b.symbolId ?? 0))

    const symbols = wsRows.map((ws) => {
      const key = `${row.id}/${ws.symbolId}`
      seen.add(key)
      const snap = snaps[key] || null
      return {
        key,
        symbolId: ws.symbolId,
        wsRow: ws,
        snap,
        link: linkOf(snap, connected, nowMs),
        /** 자리의 가동 상태 -- `wallet_symbols.status`. 지갑의 것과 2단이다.
         * ⚠ 트레이더의 `rejectedByStatus`가 아직 이 칸을 안 본다(현재위치 §7-1 3번).
         *   그래서 화면도 **보여주기만 하고 바꾸는 창구를 안 낸다.** */
        run: ws.status,
        halt: haltOf(ws),
      }
    })

    // 지갑 값(잔고·dd·계좌 레버리지)은 자리들이 **같은 값을 싣는다** -- OM 다섯이 같은
    // `LiveAccount`와 같은 `wallets.balance`를 보기 때문이다. 아무 하나에서 읽되 신선한 것을
    // 먼저 고른다: 심볼 하나가 미기동이어도 나머지가 그 값을 말해 준다.
    const lead = symbols.find((sy) => sy.link === Link.OK) || symbols.find((sy) => sy.snap) || null

    return {
      walletId: row.id,
      row,
      symbols,
      /** 지갑 값만 읽는 용도다. **자리 값(포지션·주문·시세)을 여기서 읽으면 안 된다** --
       * 그건 자리마다 다르고, 이 값은 그중 하나일 뿐이다. */
      snap: lead ? lead.snap : null,
      link: worstLinkOf(symbols, connected),
      run: row.status,
      /** 정지된 자리 수. 카드 머리에 뱃지 하나로 뜨고, 사유와 해제 버튼은 자리 줄에 있다. */
      haltedCount: symbols.filter((sy) => sy.halt.halted).length,
    }
  })

  // **walletId 오름차순.** 위에서 자리를 `symbolId` 로 정렬한 것과 같은 근거다 -- 화면 순서를
  // API 응답 순서에 맡기면 `ORDER BY` 하나가 빠져도 화면이 조용히 뒤섞인다(실제로 그랬다:
  // 대시보드에 110 이 100 위에 떴고, 설정 화면은 자기가 정렬해서 멀쩡했다).
  // 서버도 같이 고쳤지만(`WalletService.listByUserId`) 화면이 그것에 기대지 않게 둔다.
  const wallets = all
    .filter((w) => runModes.includes(w.row.runMode))
    .sort((a, b) => (a.walletId ?? 0) - (b.walletId ?? 0))

  const orphans = Object.keys(snaps)
    .filter((key) => !seen.has(key))
    .map((key) => snaps[key])

  return { wallets, orphans }
}

/** 자리들의 연결 상태를 카드 한 줄로 접는다 -- **제일 나쁜 것**을 쓴다.
 *
 * 다섯 중 하나가 미기동이면 그 카드는 미기동이라고 말해야 한다. 평균이나 다수결로 접으면
 * 안 도는 심볼 하나가 정상 카드 뒤에 숨는다.
 *
 * 자리가 아예 없으면(= `wallet_symbols` 행이 없는 지갑) 판정할 근거가 없으므로 보류한다. */
const LINK_SEVERITY = { OK: 0, UNKNOWN: 1, STALE: 2, DOWN: 3 }
export const worstLinkOf = (symbols, connected) => {
  if (!symbols || symbols.length === 0) {
    return connected ? Link.DOWN : Link.UNKNOWN
  }
  return symbols.reduce(
    (worst, sy) => (LINK_SEVERITY[sy.link] > LINK_SEVERITY[worst] ? sy.link : worst),
    Link.OK)
}

/**
 * 심볼 시세와 진입가 대비 등락 -- **출처가 `klines`(수집기)인 값들.**
 *
 * `unrealizedOf`와 **묶지 않는 이유가 신선도다.** 저쪽은 거래소가 말한 시각(`unrealizedAt`)에
 * 매달려 있고 이쪽은 봉 마감 시각(`priceAt`)에 매달려 있다. 한 덩어리로 묶어 "기준 시각 하나"로
 * 보여주면 둘 중 하나는 반드시 거짓말이 된다.
 *
 * ⚠ [2026-08-28] `priceAt`은 **봉이 닫힌 시각**이다(트레이더의 `latestClose`). 그래서 `ageMs`가
 * 곧 수집 지연이고, `PRICE_STALE_MILLIS` 하나로 봉 폭이 다른 심볼을 같이 잴 수 있다. 옛 판은
 * 봉 **시작** 시각이라 나이에 봉 폭이 통째로 들어갔고, 5분봉 심볼은 갓 들어온 값도 나이가
 * 5분이라 이 깃발이 **꺼질 수가 없었다**(BTC 만 1분봉이라 멀쩡해 보였다).
 *
 * ⚠ [2단화, 2026-08-27] **`changePct`가 여기서 빠졌다.** 자리마다 진입가가 다르므로 그 값도
 * 자리마다 하나다 -- `positionsOf`가 각 자리에 실어 준다. 시각 축이 `priceAt`인 것은 그대로라
 * 화면은 그 값을 그릴 때 이 함수의 `stale`을 같이 본다.
 *
 * `price`는 **포지션이 없어도 온다**(심볼의 사실이라 스냅샷 머리에 실린다).
 */
export const marketOf = (snap, nowMs) => {
  const price = snap?.price ?? null
  const at = snap?.priceAt ?? null
  const ageMs = at == null ? null : nowMs - at
  return {
    has: price != null,
    price,
    at,
    ageMs,
    stale: ageMs != null && ageMs > PRICE_STALE_MILLIS,
  }
}

/**
 * 국면 -- **자산 축이다.** `accountOf`(지갑 축)와 갈린다.
 *
 * 자는 자산마다 한 벌이라(트레이더의 `RegimeTilt`) 이 값은 그 심볼의 사실이다. 같은 심볼을
 * 도는 지갑이 둘이면 두 카드에 같은 값이 뜬다 -- 그게 맞다.
 *
 * ⚠ **배율을 여기서 계산하지 않는다.** `1 + k·s` 의 `k`는 엔진 인자라 화면이 알지도 못하고,
 * 여기서 다시 적으면 공식 사본이 하나 더 생긴다. 트레이더가 곱해질 수를 그대로 실어 보낸다.
 *
 * `has: false`가 두 가지를 뜻하지 않게 `off`를 갈라 둔다 --
 *
 *   off      `regime`이 `null`이다. `k = 0`이라 자를 아예 안 지었다는 뜻이다
 *   대기      스냅샷이 아직 안 왔다(트레이더 미기동)
 *
 * 둘을 "×1.00"으로 접으면 **꺼진 것**과 **켜져 있는데 중립인 것**이 같은 글자가 된다.
 *
 * `warming`은 자가 아직 다 안 찼다는 뜻이다(400봉 = 약 67일 예열). 그 구간에서 `s`가 눌리는
 * 것은 규칙이지 결함이 아니라서, 화면이 그렇게 말해 줘야 사람이 "왜 이렇게 작지"를 안 묻는다.
 */
export const regimeOf = (snap) => {
  if (!snap) return { has: false, off: false }
  const r = snap.regime
  if (!r) return { has: false, off: true }
  const up = r.up ?? 0
  const down = r.down ?? 0
  const rulers = r.rulers ?? 0
  return {
    has: true,
    off: false,
    tf: r.tf ?? null,
    s: r.s ?? null,
    up,
    down,
    rulers,
    longFactor: r.longFactor ?? null,
    shortFactor: r.shortFactor ?? null,
    warming: rulers > 0 && up + down < rulers,
  }
}

/**
 * 미실현 손익 -- **출처가 거래소인 값들.**
 *
 * 트레이더가 계산한 게 아니라 거래소 응답(`unRealizedProfit`/`markPrice`)을 통과시킨 값이다.
 * 그래서 거래소 앱에 뜨는 숫자와 맞는다.
 *
 * ⚠ **`marketOf().changePct`와 교차 검산하면 안 된다.** 기준가가 다르다 -- 이쪽은 mark price,
 * 저쪽은 최종 체결가(1분봉 종가)다. 부호는 같지만 값이 어긋나고, 움직임이 아주 작을 때는
 * 부호까지 갈릴 수 있다.
 *
 * `ratio`가 없을 수 있다 -- dd 기준잔고가 아직 없는 지갑이다(분모 없음). `pnl`은 있는데
 * `ratio`만 비는 게 정상 상태다.
 *
 * [합산 등락, 2026-09-05 주인 지시] `changePct`는 이 심볼 **순포지션의 등락**이다 -- 거래소
 * 평단 대비 mark 라 `pnl`과 **같은 축**이고, 그래서 `pnl = 진입명목 x changePct` 가 맞는다.
 *
 * ⚠ **자리 줄의 `positionsOf().changePct`와 기준가가 다르다.** 저쪽은 자리 진입가 대비 1분봉
 * 종가다. 두 값을 교차 검산하면 안 되고, 화면은 분모 이름을 붙여 그린다(`% 진입`).
 * 자리 둘의 등락 사이에 있는 것이 정상이다 -- 명목 가중평균이라서다.
 *
 * ⚠ [미실현 축, 2026-09-05] **인자가 스냅샷이다 -- 자리가 아니다.** 거래소는 one-way 넷이라
 * 심볼당 포지션을 하나 주고, 4h 껍질과 1h 얹기는 그 하나의 평단에 이미 섞여 있다. 그래서 이
 * 값에는 자리 개념이 없다.
 *
 * 2026-08-27 2단화가 이 인자를 자리 하나로 바꿨었는데 트레이더 쪽 관측이 심볼 축으로 남아
 * 있어서, **자리가 둘인 심볼은 미실현이 통째로 안 떴다** -- 대조가 합계와 자리 하나를 견줘서
 * 두 자리 다 떨어졌다(BNB 0.62 + 0.28 에 관측 0.90).
 *
 * 자리마다 갈라 주려면 화면이나 트레이더가 `수량 x (mark - 진입가)`를 다시 써야 하는데, 그건
 * `PositionCalc.priceChangeRatio` 사본이라 안 한다. 게다가 갈라 낸 값들의 합은 거래소 숫자와
 * 안 맞는다 -- 거래소는 자기 평단을 쓰고 펀딩이 섞여 있다.
 *
 * 자리 축에 남는 값은 등락(`positionsOf().changePct`)뿐이다. 그건 자리마다 진입가가 달라
 * **정말로 자리 값**이다.
 */
export const unrealizedOf = (snap, nowMs) => {
  const pnl = snap?.unrealizedPnl ?? null
  const at = snap?.unrealizedAt ?? null
  const ageMs = at == null ? null : nowMs - at
  return {
    has: pnl != null,
    pnl,
    ratio: snap?.unrealizedRatio ?? null,
    /** 순포지션 등락(%) -- **단위가 %다**(2.33 = 2.33%). 트레이더가 이미 x100 한 값이다. */
    changePct: snap?.unrealizedChangePct ?? null,
    /** 거래소가 손익을 계산한 기준가. 화면의 `price`(1분봉 종가)와 다른 값이다. */
    markPrice: snap?.markPrice ?? null,
    at,
    ageMs,
    stale: ageMs != null && ageMs > UNREALIZED_STALE_MILLIS,
  }
}

/**
 * 이 심볼의 **자리들** -- 확정본은 한 심볼에 4h 껍질과 1h 얹기가 각자 선다.
 *
 * 트레이더가 `positions` 배열로 보낸다(2026-08-27, `OrderManager.publishState`). 옛 판은
 * `position` 하나였고 **첫 자리만** 실렸다 -- 얹기가 든 동안 화면이 노출을 절반으로 보여줬다.
 *
 * 비면 빈 배열이다. `null`(못 받음)과 `[]`(포지션 없음)을 가르는 것은 `link`가 이미 한다 --
 * 스냅샷 자체가 없으면 그쪽이 미기동으로 말하므로 여기서 또 가르지 않는다.
 *
 * 정렬은 **트레이더가 준 순서 그대로**다. `live_positions` 조회 순서이고, 그 순서에 뜻이
 * 있으면 트레이더가 지고 화면은 안 지어낸다.
 */
/** 진입 명목 = |수량| x 진입가.
 *
 * [2026-08-28 주인 지시] 자리 줄이 코인 수량만 말했다 -- `27.58000000` 이 얼마짜리인지는
 * 사람이 진입가를 곱해야 알았다. 자산이 다섯이면 그 암산을 다섯 번 한다.
 *
 * 🔴 **진입가로 곱한다 -- mark 가 아니다.** 둘은 다른 물음의 답이다.
 *
 *   진입가 x 수량   이 자리가 **얼마를 걸었나**. 사이징(자본/5 x GZ x 국면무게 x 배수)이
 *                  제대로 먹었는지 보는 값이고, 시세가 움직여도 안 변한다
 *   mark  x 수량   **지금 노출**. 안전망이 재는 값인데, 그건 이미 카드 위의
 *                  「계좌 레버리지」가 계좌 축으로 접어서 말하고 있다
 *
 * 이 줄은 `@ 진입가` 옆이라 **진입 시점의 사실만 담는 줄**이다. 여기에 mark 로 곱한 값을
 * 넣으면 한 줄 안에서 기준 시각이 갈린다 -- `unrealizedOf` 와 `changePct` 를 굳이 다른 줄로
 * 가른 것과 같은 이유다.
 *
 * ⚠ [자리 진입가, 2026-09-05] **`slotEntryPrice` 로 곱한다 -- 평단이 아니다.** 트레이더가 자리
 * 줄에 싣는 진입가가 그 칸으로 바뀌었다. 옛 판은 거래소 평단을 곱해서 "이 자리가 얼마를
 * 걸었나" 가 자리 둘 다 틀렸다(BNB 4h 가 199.40 대신 204.76 이었다).
 *
 * **절대값을 쓴다.** 방향은 `side` 가 이미 말하고, 명목이 음수로 찍히면 "빚" 으로 읽힌다.
 *
 * 단위를 안 붙인다 -- 이 화면 어디에도 quote 통화 이름이 안 실려 온다(스냅샷에도
 * `wallets` 행에도 없다). 잔고·시세와 **같은 단위**라는 것이 이 화면의 전제이고,
 * 여기만 'USDT' 라고 적으면 그 전제를 한 곳에서만 주장하는 꼴이 된다. */
const notionalOf = (p) => {
  const qty = p?.qty
  const price = p?.slotEntryPrice
  if (typeof qty !== 'number' || typeof price !== 'number') {
    return null
  }
  if (!Number.isFinite(qty) || !Number.isFinite(price)) {
    return null
  }
  return Math.abs(qty) * price
}

export const positionsOf = (snap) => {
  const list = snap?.positions
  if (!Array.isArray(list)) {
    return []
  }
  return list.map((p) => ({
    ...p,
    /** 방향 보정된 등락률. **단위가 %다**(2.0 = 2%) -- 100을 다시 곱하면 안 된다.
     * 트레이더 `priceChangePct`가 `PositionCalc.priceChangeRatio × 100`으로 이미 만든 값이다. */
    changePct: p?.priceChangePct ?? null,
    /** 진입 명목. 못 구하면 `null` 이고 화면이 그 칸을 통째로 뺀다. */
    notional: notionalOf(p),
  }))
}

/**
 * 계좌 축 -- 안전망이 보는 그 값이다(`LiveAccount`). **지갑 값이라 자리 다섯이 같은 값을 싣는다.**
 *
 * `null`이면 트레이더가 아직 계좌를 못 쟀다는 뜻이다. 0으로 그리면 "레버리지 0배"와 구별이
 * 안 되므로 화면이 "관측 대기"라고 말해야 한다 -- 그 판정을 여기서 내준다.
 *
 * `headroom`은 **전량 청산선까지 남은 배수**다. 진입 금지선(`defendLeverage`)은 넘어도
 * 되돌아올 수 있지만 청산선은 되돌릴 수 없어서, 사람이 봐야 하는 여유는 그쪽 것이다.
 */
export const accountOf = (snap) => {
  const a = snap?.account
  if (!a || a.leverage == null) {
    return {
      has: false,
      leverage: null,
      defendLeverage: a?.defendLeverage ?? null,
      maxLeverage: a?.maxLeverage ?? null,
    }
  }
  return {
    has: true,
    equity: a.equity ?? null,
    grossNotional: a.grossNotional ?? null,
    leverage: a.leverage,
    defendLeverage: a.defendLeverage ?? null,
    maxLeverage: a.maxLeverage ?? null,
    at: a.at ?? null,
    blocked: a.defendLeverage != null && a.leverage > a.defendLeverage,
    headroom: a.maxLeverage != null ? a.maxLeverage - a.leverage : null,
  }
}

/**
 * 크기 손잡이 -- **`wallets` 행이 정본이다. 스냅샷이 아니다.**
 *
 * `accountOf`와 짝처럼 보이지만 출처가 반대다: 저건 트레이더가 발행하는 실시간 관측값이고
 * 이건 사람이 DB에 적어 둔 설정값이다. 그래서 **트레이더가 죽어 있어도 이 줄은 선다** --
 * 미기동 지갑에서 "무슨 설정으로 뜰 예정인가"를 보는 것이 이 줄의 쓸모이고, 자리 목록을
 * `wallet_symbols`에서 만드는 것과 같은 근거다(제일 잘 죽는 화면을 보고 출처를 고른다).
 *
 * `caps` 셋은 **0이 "끔"이다** -- 트레이더의 어휘 그대로다(엔진이 `volumeCapRatio <= 0.0`을
 * 캡 없음으로 본다). `on`을 여기서 판정하는 이유는 화면이 그 규칙을 다시 적으면 언젠가
 * 한쪽만 고쳐지기 때문이다.
 *
 * `factor`가 `null`이면 빈 문자열이라는 뜻이고, 그게 곧 **전 눈금 1.0**이다
 * (`StrategyAssembly.parseSlotWeights`의 같은 규약). 화면이 그 뜻을 글자로 풀어 준다.
 *
 * ⚠ 옛 판은 이 셋이 `user_symbol_configs`에 있었다(`usc.page.js`). 표가 `(user, symbol)`
 *   축이라 자산 다섯이면 유저당 다섯 행이었고, `saving_ratio`는 `run_mode`를 몰랐다.
 */
export const sizingOf = (row) => {
  const cap = (label, value, unit) => {
    const v = optNum(value)
    return { label, value: v, unit, on: v != null && v > 0 }
  }
  return {
    multiple: optNum(row?.slotMultiple),
    factor: optStr(row?.slotFactor),
    caps: [
      cap('거래량 캡', row?.volumeCapRatio, 'ratio'),
      cap('명목 상한', row?.maxNotional, null),
      cap('마스터 이체', row?.savingRatio, 'ratio'),
    ],
  }
}

/** 종료된 지갑의 거래소 관측값이 이만큼 낡으면 흐리게 표시한다.
 *
 * 출처는 `unrealizedOf`와 같은 `scanVanishedPosition`(1분 주기)인데 **임계가 다르다.**
 * 저쪽 5분은 미결 주문이 관측을 최대 10분까지 미룰 수 있어서 잡은 값이다. 종료된 지갑은
 * 우리 미결이 0인 게 `finSettled`의 전제라 그 지연이 원천적으로 없다 -- 1분 주기가 지켜지므로
 * 세 번 빠지면 조회가 실제로 실패하고 있는 것이다. */
export const OBSERVED_STALE_MILLIS = 3 * 60 * 1000

/**
 * 종료 상태와 거래소 실물 관측값.
 *
 * == "포지션 없음"이 두 뜻이라는 문제 ==
 *
 *   가동 중 -> 관측했고 없었다
 *   종료 후 -> 우리 미러가 없다 (거래소엔 있을 수 있다)
 *
 * 겉보기가 같아서 사람이 후자를 "비어 있다"로 읽는다. `settled`가 그 둘을 가른다.
 *
 * == `observed`는 `position`이 아니다 ==
 *
 * **엔진이 낸 게 아니다.** 종료 뒤 사람이 직접 연 포지션이 여기 잡힌다. 화면에서 반드시
 * 지갑 포지션과 다른 자리·다른 이름으로 그려야 한다 -- 같은 칸에 넣으면 FIN의 정의
 * ("엔진이 낸 것만 우리 것")를 화면이 뒤집는다.
 *
 * 서버가 종료된 지갑에서만 싣는다. 가동 중에는 미러가 정본이고, 둘을 나란히 두면 사람이
 * 대조하기 시작한다(`price`/`markPrice` 교차 검산 금지와 같은 이유).
 */
export const settlementOf = (snap, nowMs) => {
  const settled = Boolean(snap?.settled)
  const o = snap?.observed
  const at = o?.at ?? null
  const ageMs = at == null ? null : nowMs - at
  return {
    settled,
    /** 거래소에 실물이 있다. `settled`가 false면 항상 false다(서버가 안 싣는다). */
    has: Boolean(o),
    side: o?.side ?? null,
    qty: o?.qty ?? null,
    entryPrice: o?.entryPrice ?? null,
    markPrice: o?.markPrice ?? null,
    pnl: o?.unrealizedPnl ?? null,
    at,
    ageMs,
    stale: ageMs != null && ageMs > OBSERVED_STALE_MILLIS,
  }
}

/**
 * 확정 잔고 합 -- 서브 지갑(선물) + 마스터(현물).
 *
 * == 스냅샷을 안 쓴다 ==
 *
 * **`rows`(DB)만 더한다.** 스냅샷은 트레이더가 떠 있어야 오므로, 그걸 더하면 미기동 지갑이
 * 합계에서 통째로 빠져 **화면이 "돈이 사라졌다"로 보인다.** 지갑 카드가 미기동일 때 칩 하나가
 * 비는 것과 뿌리는 같지만, 이쪽은 **총액이 틀린다.**
 *
 * `wallets.balance`는 트레이더가 거래소를 부른 자리마다(기동·진입·청산·60분 스냅샷) 적어두는
 * 실잔고라, 트레이더가 지금 떠 있지 않아도 마지막 값이 남아 있다.
 *
 * == run_mode를 거른다 -- 목록과 **다른 선**이다 ==
 *
 * `DryRunExchangeAdapter.queryBalance`는 상수를 돌려주고 그 값이 `wallets.balance`에 실제로
 * 적재된다(러너의 `verify`가 DRILL도 통과시킨다). 안 거르면 **드릴 지갑의 가짜 돈이 총액에
 * 낀다.**
 *
 * ⚠ **테스트넷도 같은 이유로 뺀다**(2026-08-10). 출처는 다르지만(거래소가 실제로 돌려준 값)
 * 총자산이 아니라는 점에서 같다. 예전엔 목록과 같은 선(`LIVE_RUN_MODES`)을 써서
 * **실계좌 넷이 0.00인데 테스트넷 4,273.39가 "확정 잔고 합"에 들어갔다** -- 계좌가 비었다는
 * 사실이 화면에서 가려졌다. 지금은 `TOTAL_RUN_MODES`(그 상수 주석에 근거).
 *
 * **버리지는 않는다.** 테스트넷 몫은 따로 낸다 -- 청산·이체 시험 결과를 보려면 그 잔고도
 * 봐야 한다. 화면이 "합계에 안 들어감"을 글자로 적는 것이 짝이다(숫자만 따로 두면 눈으로 더한다).
 *
 * == 선물과 현물을 갈라 낸다 ==
 *
 * 서브는 USDM 선물 지갑이고 마스터는 현물이다. 같은 USDT라 더하는 건 맞지만 **화면이
 * "선물 합 + 현물"임을 드러내야 한다** -- 합쳐서 숫자 하나만 내면 어디에 얼마가 있는지가
 * 사라지고, `savingRatio`로 돈이 마스터에 쌓이는 게 안 보인다.
 *
 * == 신선도는 안 낸다 (주인 확정) ==
 *
 * `wallets.balance`에는 갱신 시각 컬럼이 없고, 있는 그대로 더한다. 지갑별 신선도는 카드가
 * 이미 말하고, 마스터는 자기 줄에 `regAt`을 단다.
 *
 * == 미실현은 안 들어간다 ==
 *
 * 그래서 이름이 "합산 잔고"가 아니라 **확정 잔고 합**이다. 포지션을 들고 있는 동안의
 * 평가손익은 이 숫자에 없다 -- 고장이 아니라 이 값의 성질이고, 화면이 그렇게 불러야 한다.
 *
 * @param rows    /wallet/user/{userId} 응답 (WalletsRow 배열)
 * @param masters /ma/user/{userId} 응답의 `masters` -- `[{ account, balance }]`, 거래소마다 하나
 */
export const totalsOf = (rows, masters) => {
  const all = rows || []
  const live = all.filter((r) => TOTAL_RUN_MODES.includes(r.runMode))

  // `?? 0`으로 받는다 -- 옛 트레이더가 아직 그 칸을 안 쓰는 지갑은 컬럼 기본값 0이고,
  // 그건 "합계에 0을 보탠다"가 맞다. null로 두고 합계를 null로 만들면 지갑 하나 때문에
  // 총액이 통째로 사라진다.
  const sumBalance = (list) => list.reduce((sum, r) => sum + (r.balance ?? 0), 0)

  const futures = sumBalance(live)

  /**
   * 🔴 **거래소가 축이다**(주인 확정 2026-09-02).
   *
   * 옛 모양은 `선물 합 · 현물 합` 이었다. 거래소가 하나일 때는 그게 곧 "서브 : 마스터"라
   * 뜻이 통했는데, 둘이 되는 순간 **선물 합 하나에 두 거래소가 섞인다** -- 어느 쪽이 비었는지
   * 안 보이고, 현물 쪽은 아예 바이낸스 것만 있었다.
   *
   * 그래서 축을 뒤집는다. **거래소마다 한 줄**, 그 안에서 마스터(현물)와 거래지갑(선물)으로
   * 가른다. 돈이 어디 있는지가 곧 "어느 거래소의 어느 계좌"이기 때문이다.
   *
   * {{{
   *   바이낸스 429.87  (마스터 63.76 · 거래지갑 366.11)
   *   Bitget     0.00  (마스터 관측 대기 · 거래지갑 0.00)
   * }}}
   *
   * == 갈래가 셋인 값들 ==
   *
   * `spot`(마스터 잔고)은 **`null`이 될 수 있다.** 계정은 있는데 관측 행이 아직 없는 경우이고
   * (지금 BTG 마스터가 그렇다 -- `MasterBalanceFetcher`가 바이낸스만 관측한다), 그것은
   * "0원"과 다른 사실이다. 0으로 접으면 화면이 "비었다"고 말하는데 진실은 "모른다"이다.
   *
   * 거래소 목록은 **지갑과 마스터의 합집합**이다. 한쪽에만 있는 거래소도 줄을 얻는다 --
   * 마스터만 있고 지갑이 없으면 그 사실이 보여야 하고, 반대도 같다.
   *
   * 정렬은 **코드 오름차순**이다. 금액순으로 하면 잔고가 오갈 때마다 줄이 자리를 바꿔서
   * 사람이 같은 자리에서 같은 거래소를 못 읽는다.
   */
  const byExchangeOf = (walletList, masterList) => {
    const acc = new Map()
    const at = (code) => {
      const key = code || ''
      if (!acc.has(key)) {
        acc.set(key, {
          exchangeId: key,
          futures: 0,
          walletCount: 0,
          /** 관측된 마스터 잔고의 합. 관측이 하나도 없으면 `null`(= 모른다). */
          spot: null,
          /** 계정은 있는데 관측이 없는 마스터 수. */
          spotPending: 0,
          masterCount: 0,
        })
      }
      return acc.get(key)
    }

    walletList.forEach((r) => {
      const cur = at(r.exchangeId)
      cur.futures += (r.balance ?? 0)
      cur.walletCount += 1
    })

    ;(masterList || []).forEach((m) => {
      const cur = at(m?.account?.exchangeId)
      cur.masterCount += 1
      if (m?.balance) {
        cur.spot = (cur.spot ?? 0) + (m.balance.balance ?? 0)
      }
      else {
        cur.spotPending += 1
      }
    })

    return Array.from(acc.values())
      .map((e) => ({ ...e, total: e.futures + (e.spot ?? 0) }))
      .sort((a, b) => a.exchangeId.localeCompare(b.exchangeId))
  }

  // 관측이 아직 없으면 마스터 몫은 없다 -- 0으로 세는 게 아니라 **모른다.** 그래도 총액은
  // 낸다(서브만으로도 뜻이 있다). 화면이 마스터 줄에서 "관측 대기"라고 말한다.
  //
  // 🔴 마스터가 **여럿**이다(거래소마다 하나). 그래서 셋을 갈라 낸다:
  //   · `spot`        -- 잔고가 **관측된** 마스터들의 합. 하나도 없으면 `null`(= 모른다).
  //   · `spotPending` -- 계정은 있는데 잔고 행이 없는 마스터 수. **0으로 접지 않는다** --
  //                      "관측 대기"와 "잔고 0"은 다른 사실이고, 지금 BTG 마스터가 정확히
  //                      앞쪽이다(`MasterBalanceFetcher`가 아직 바이낸스만 관측한다).
  //   · `masters`     -- 화면이 줄마다 그릴 원본. 여기서 이름·거래소를 붙여 통과시킨다.
  const masterList = masters || []
  const observed = masterList.filter((m) => m?.balance)
  const spot = observed.length === 0
    ? null
    : observed.reduce((sum, m) => sum + (m.balance.balance ?? 0), 0)

  // 총액 밖 -- **목록에는 있고 합계에는 없는** 지갑들. `LIVE_RUN_MODES`에서 `TOTAL_RUN_MODES`를
  // 뺀 차집합이라 두 상수가 어긋나도 이 계산은 저절로 따라온다(둘을 각자 적으면 안 따라온다).
  const excludedModes = LIVE_RUN_MODES.filter((m) => !TOTAL_RUN_MODES.includes(m))
  const excluded = all.filter((r) => excludedModes.includes(r.runMode))

  return {
    futures,
    spot,
    total: futures + (spot ?? 0),
    /** 합계에 실제로 들어간 지갑 수. 목록과 다르면 run_mode 필터가 뭔가를 걸렀다는 뜻이다. */
    walletCount: live.length,
    /** 🔴 **화면의 본줄.** 거래소마다 `{ futures, spot, total, walletCount, spotPending }`. */
    byExchange: byExchangeOf(live, masterList),
    /** 마스터 몫이 빠진 총액인가 -- 화면이 그걸 드러내야 한다. */
    spotMissing: spot == null,
    /** 계정은 있는데 아직 관측이 없는 마스터 수. 0보다 크면 현물 합이 **덜 세어진 값**이다. */
    spotPending: masterList.length - observed.length,
    /** 마스터 줄의 원본 -- 거래소별로 한 줄씩 그린다. */
    masters: masterList,
    /**
     * 목록엔 뜨지만 **총액에 안 들어간** 몫(지금은 테스트넷).
     *
     * `null`이 아니라 `count: 0`으로 낸다 -- 화면이 "없음"과 "0원"을 구별해 그릴 수 있어야
     * 하고, 그 판정을 화면이 아니라 여기서 한다. 해당 지갑이 하나도 없으면 줄 자체를 안 그린다.
     */
    excluded: {
      modes: excludedModes,
      count: excluded.length,
      futures: sumBalance(excluded),
      /** 여기도 같은 이유로 가른다 -- 테스트넷 지갑이 두 거래소에 다 있다.
       * 마스터는 안 넘긴다: 마스터에 run_mode가 없어서 "테스트넷 마스터"라는 것이 없다. */
      byExchange: byExchangeOf(excluded, []),
    },
  }
}

/** 스냅샷과 DB가 정지 여부를 다르게 말하고 있는가.
 *
 * **내용은 비교하지 않는다** -- 스냅샷의 `halted`는 사유 문자열이고 DB는 code/detail로
 * 갈려 있어 애초에 같은 모양이 아니다. "있다/없다"만 본다.
 *
 * 어긋나는 건 대개 시차다(스냅샷 5초 vs DB 폴링). 그 시차를 메우려고 DB를 다시 읽는 것이지,
 * 어긋남 자체를 사고로 보는 게 아니다. */
export const disagreesOnHalt = (view) =>
  view.wallets.some((w) =>
    w.symbols.some((sy) => sy.snap && Boolean(sy.snap.halted) !== sy.halt.halted))
